import * as React from 'react';

import { ApiError } from '@/domains/api';
import { encodeAnswerAs, type UiValue } from '@/domains/survey';
import type { InternalFieldType, InternalPayload } from '../../api/internal-field-contracts';
import type {
  ICurrentCell,
  IListCell,
  IListEditCell,
  IListEditCellRef,
  IListEditConflict,
  IListEditRefusal,
  IListEditResult,
  IListField,
  IListRow
} from '../../api/lists-contracts';
import { listsApi } from '../../api/lists-api';
import { NOT_ARRIVED } from '../../components/field-controls';
import { refusedCellText } from '../../correction-refusal';
import {
  decodeInternalValue,
  encodeInternalValue,
  isMalformedInternal,
  sameInternalValue,
  type InternalUiValue
} from '../../internal-field-values';
import { NAME_FIELD } from '../../list-criteria';
import { REMOVED_ACCOUNT, countOf } from '../../lists';
import { refusalMessage } from '../../refusal';
import { rowKeyOf, type IAdoptedCell } from './use-list-rows';

/**
 * A list's edits (SR-230, design `docs/superpowers/specs/2026-10-08-lists-reports-import-ui.md`
 * §3.6; survey-api's design §7): the cells a reader changed, held as drafts until *Save data*, then
 * written as **one audited batch**, each cell at the version it was read at, and survey-api's answer
 * read per cell.
 *
 * **A draft is keyed by participant, survey and field** — a row's key and its column — and keeps
 * the cell as it was read when the draft began (`base`): the version a save quotes and what "edited
 * back" compares with. A poll that patches the row under a draft moves the row, never the draft: a
 * version that moved makes the save a conflict, which is the point (§3.5). **A draft equal to the
 * stored value is no longer pending**, and an unticked tick over a cell holding nothing is the cell
 * as it was. **Malformed input is held** (`isMalformedInternal`, or a date input's
 * `validity.badInput`): never sent and never cleared — the payload rule of the hub's fields
 * (`internal-field-values.ts`), not validation; survey-api judges every value that can be sent.
 *
 * **The answer, per cell** (§3.6): `applied` and `unchanged` adopt the version and drop the draft;
 * each `conflict` is asked about one at a time, in the order survey-api sent them — *Keep mine*
 * resends that cell alone at `current.version`, with the note "Kept over <who>'s save"; *Take
 * theirs* (and *Cancel*, AC3) draws `current` and sends nothing. Then **one poll read**, so a row the
 * save took out of the list is struck, and one status line says it all — a sent cell the answer
 * names nowhere is said as not answered, and stays a draft. A refusal sends nothing and keeps every
 * draft: a 400 said on the cell its path names (`cells[3].value`), a 403 atop the grid, an answer
 * that never arrived (the hub's `NOT_ARRIVED`) with the page read again — and **once that read
 * lands, a batch it shows landed is settled**: each sent draft whose value the cell now holds is
 * dropped, the rest based on the cell as read, so the next press is not a conflict with the
 * reader's own save. A refused *Keep mine* is said by its cell, the refusals of one press together.
 *
 * **An answer is a cell like any other** (SR-403): survey-api's catalogue marks a `q.*` field
 * `editable` with its value tag as `editType`, and the cell is typed into the staff field's control
 * for that shape (`num` a decimal's, `opts` a select's or a list of ticks') and sent as the
 * respondent route's payload (`encodeAnswerAs`) — survey-api corrects it, no reason asked. An answer
 * cell survey-api could not correct comes back **`refused`** with a code (a draft, an answer the
 * response's logic hides, a blocking finding, a matrix code): nothing of it was written, so it is
 * said on its cell, like a 400, and its draft is kept — never dropped unasked.
 *
 * Rejected: a save per cell. The client's AC is one batch — one row in the audit for one press of
 * *Save data* — and a conflict found before any write means survey-api never rolls a cell back.
 */

/** survey-api's *Assigned to* — the one editable cell that is neither a staff field nor an answer
 *  (`lists.assign`). */
export const ASSIGNED_TO = 'sys.assigned_to';

/** survey-api's prefix for an answer's field (`ListFieldRules.AnswerPrefix`): no staff field's code
 *  may take it, so it alone says a field is an answer. */
const ANSWER_PREFIX = 'q.';

/** An answer cell's value tag, as the catalogue's `editType` names it (SR-403) — the respondent
 *  route's tags a grid writes; `cells` is a summary's, which no cell edits. */
type AnswerTag = 'text' | 'int' | 'num' | 'pct' | 'money' | 'date' | 'opts';

/** The staff field control an answer's tag is typed into — one per shape; `opts` is a select or a
 *  list of ticks, by the field's kind. */
const CONTROL_OF_TAG: Readonly<Record<Exclude<AnswerTag, 'opts'>, InternalFieldType>> = {
  text: 'text',
  int: 'int',
  num: 'decimal',
  pct: 'percent',
  money: 'money',
  date: 'date'
};

function isAnswerTag(tag: string): tag is AnswerTag {
  return tag === 'opts' || tag in CONTROL_OF_TAG;
}

/** Whether a field is a survey answer — a `q.*` field. */
export function isAnswerField(field: Pick<IListField, 'code'>): boolean {
  return field.code.startsWith(ANSWER_PREFIX);
}

/** An editable answer's value tag, or null — a staff field, or a tag this app has no control for. */
export function answerTagOf(
  field: Pick<IListField, 'code' | 'editable' | 'editType'>
): Nullable<AnswerTag> {
  if (!field.editable || !isAnswerField(field) || field.editType === null) return null;
  return isAnswerTag(field.editType) ? field.editType : null;
}

/** The staff-field types a list's cell is edited as. Rich text is read and edited on the record
 *  page — not in the catalogue by design (§3.5); a long text is a list field since SR-408 (R4) and
 *  is edited like a text, its line breaks kept (the owner's Q2). */
const CELL_TYPES: ReadonlySet<string> = new Set([
  'text',
  'long_text',
  'int',
  'decimal',
  'money',
  'percent',
  'date',
  'bool',
  'select',
  'multi_select'
]);

/** How a cell is edited — the control it is typed into: its staff field's type (an answer's, the
 *  one its tag takes — a `text` tag on a `long_text` field, a `text_area` answer, the long text's),
 *  or an account (*Assigned to*). */
export type CellEditKind = InternalFieldType | 'assignee';

/** A column's edit, or null where it is read-only — not editable, or a type no cell control takes. */
export function editKindOf(
  field: Pick<IListField, 'code' | 'kind' | 'editable' | 'editType'>
): Nullable<CellEditKind> {
  if (!field.editable) return null;
  if (field.code === ASSIGNED_TO) return 'assignee';
  if (isAnswerField(field)) {
    const tag = answerTagOf(field);
    if (tag === null) return null;
    if (tag === 'opts') return field.kind === 'multi_enum' ? 'multi_select' : 'select';
    // Its payload is still built from the tag (`encodeAnswerAs`); only the control is a long text's.
    if (tag === 'text' && field.kind === 'long_text') return 'long_text';
    return CONTROL_OF_TAG[tag];
  }
  return field.editType !== null && CELL_TYPES.has(field.editType)
    ? (field.editType as InternalFieldType)
    : null;
}

/** A row's raw value as its type's payload — null where the cell holds nothing, or a value of
 *  another shape (which the grid draws as written). */
function payloadOf(type: InternalFieldType, value: unknown): Nullable<InternalPayload> {
  if (value === null || value === undefined) return null;
  switch (type) {
    case 'bool':
      return typeof value === 'boolean' ? { t: 'bool', v: value } : null;
    case 'multi_select':
      return Array.isArray(value) && value.every((code) => typeof code === 'string')
        ? { t: 'multi_select', v: value as string[] }
        : null;
    case 'int':
    case 'decimal':
    case 'money':
    case 'percent':
      return typeof value === 'number' ? ({ t: type, v: value } as InternalPayload) : null;
    default:
      return typeof value === 'string' ? ({ t: type, v: value } as InternalPayload) : null;
  }
}

/** A stored cell as a control's starting value; an account's is its id, `''` for nobody. */
export function storedUi(kind: CellEditKind, cell: Nullable<IListCell> | undefined): InternalUiValue {
  const value = cell?.v;
  if (kind === 'assignee') return typeof value === 'string' ? value : '';
  return decodeInternalValue(payloadOf(kind, value), kind);
}

/** What a control's value sends — the `{t, v}` payload, an account id, `null` to clear — or that it
 *  is held. */
export type Staged = { held: true } | { held: false; value: unknown };

/** `answer` is an answer cell's tag — its value goes as the respondent route's payload. */
export function stagedOf(
  kind: CellEditKind,
  ui: InternalUiValue,
  badInput = false,
  answer: Nullable<AnswerTag> = null
): Staged {
  if (kind === 'assignee') return { held: false, value: typeof ui === 'string' && ui !== '' ? ui : null };
  if (badInput || isMalformedInternal(kind, ui)) return { held: true };
  if (answer !== null) return { held: false, value: encodeAnswerAs(answerUi(kind, ui), answer) };
  return { held: false, value: encodeInternalValue(kind, ui) };
}

/** A control's value as the respondent's encoder takes it: a select's code as a list of one. */
function answerUi(kind: InternalFieldType, ui: InternalUiValue): UiValue {
  if (kind === 'select') return typeof ui === 'string' && ui !== '' ? [ui] : [];
  if (Array.isArray(ui)) return ui;
  return typeof ui === 'string' ? ui : '';
}

/**
 * A sent value — or a conflict's `current`, survey-api's stored document — as the row holds it: a
 * payload's `v`, an account id, `null` for a clear. An answer's `opts` on a select is its one code,
 * the way survey-api draws a single choice in a row.
 */
export function rawOf(kind: CellEditKind, value: unknown): unknown {
  if (value === null || value === undefined) return null;
  if (kind === 'assignee') return value;
  if (typeof value !== 'object' || !('v' in value)) return null;
  const { t, v } = value as { t?: unknown; v: unknown };
  if (t === 'opts' && kind === 'select') return Array.isArray(v) ? (v[0] ?? null) : null;
  return v;
}

/** Two row values alike — a list of codes in its order, which survey-api keeps. */
function sameRaw(a: unknown, b: unknown): boolean {
  if (Array.isArray(a) && Array.isArray(b)) {
    return a.length === b.length && a.every((one, index) => one === b[index]);
  }
  return (a ?? null) === (b ?? null);
}

/**
 * Two long texts alike as a textarea hands them back (SR-408): its value turns a stored CR LF into
 * LF, and the encoder trims the ends — so a migrated note typed into and put back would otherwise
 * be a change nobody made, and Save data would rewrite it under the editor's name.
 */
function sameLongText(a: unknown, b: unknown): boolean {
  const plain = (text: unknown) =>
    typeof text === 'string' ? text.replaceAll('\r\n', '\n').replaceAll('\r', '\n').trim() : '';
  return plain(a) === plain(b);
}

/** Whether a value sent would leave the cell as it was read. */
function sameAsStored(
  kind: CellEditKind,
  value: unknown,
  base: Nullable<IListCell>,
  answer: Nullable<AnswerTag>
): boolean {
  if (kind === 'assignee') return (value ?? null) === (typeof base?.v === 'string' ? base.v : null);
  if (kind === 'long_text') return sameLongText(rawOf(kind, value), base?.v);
  if (answer !== null) return sameRaw(rawOf(kind, value), base?.v);
  const stored = payloadOf(kind, base?.v);
  // Unticked is the value `false`, but over a cell holding nothing it is the cell as it was drawn.
  if (kind === 'bool' && stored === null) return (value as Nullable<InternalPayload>)?.v !== true;
  return sameInternalValue(value as Nullable<InternalPayload>, stored);
}

/** One pending cell. */
export interface ICellDraft {
  key: string;
  participantId: string;
  survey: string;
  field: string;
  kind: CellEditKind;
  /** An answer cell's value tag (SR-403) — its value goes as the respondent route's payload. */
  answer: Nullable<AnswerTag>;
  /** The person as the row named them — the conflict's sentence and a refusal's. */
  name: string;
  /** The column's label — a refused *Keep mine*'s sentence. */
  label: string;
  ui: InternalUiValue;
  staged: Staged;
  /** The cell as read when the draft began: the version a save quotes (0 for an empty cell). */
  base: Nullable<IListCell>;
}

export function draftKey(cell: { participantId: string; survey: string; field: string }): string {
  return `${rowKeyOf(cell)}:${cell.field}`;
}

/** The row's person in words — its name cell, which every read asks for. */
export function nameOf(row: IListRow): string {
  const name = row.cells[NAME_FIELD]?.v;
  return typeof name === 'string' && name.trim() !== '' ? name : 'this row';
}

/** Who saved a cell, in words: the email, or "an account since removed". */
export function authorOf(current: ICurrentCell): string {
  return current.setBy?.email ?? REMOVED_ACCOUNT;
}

/** A conflict's value now, as the row holds it. */
export function currentRaw(kind: CellEditKind, current: ICurrentCell): unknown {
  return rawOf(kind, current.v);
}

/** What one Save data press settled, said once its conflicts are answered. */
interface ITally {
  saved: number;
  kept: number;
  taken: number;
  held: number;
  /** Answer cells survey-api could not correct, by name — each said on its cell, its draft kept,
   *  and named here too, since a cell staged on another page or in a column since hidden is not
   *  drawn to say it. */
  refused: string[];
  /** Sent, and named by none of the answer's lists. */
  unanswered: number;
}

const NO_TALLY: ITally = { saved: 0, kept: 0, taken: 0, held: 0, refused: [], unanswered: 0 };

/** A few cells by name — "Firm Name for Jane Doe, Gender for John Roe and 3 more". */
function someCells(names: readonly string[]): string {
  const shown = names.slice(0, 3);
  const more = names.length - shown.length;
  return `${shown.join(', ')}${more > 0 ? ` and ${more.toLocaleString('en-US')} more` : ''}`;
}

/** The status line (§3.6): "3 changes saved · 1 kept over a colleague's · …". */
function tallySentence(tally: ITally): string {
  return [
    tally.saved > 0 ? `${countOf(tally.saved, 'change')} saved` : null,
    tally.kept > 0 ? `${tally.kept.toLocaleString('en-US')} kept over a colleague’s` : null,
    tally.taken > 0
      ? `${tally.taken.toLocaleString('en-US')} left as a colleague saved ${tally.taken === 1 ? 'it' : 'them'}`
      : null,
    tally.held > 0
      ? `${countOf(tally.held, 'entry', 'entries')} held — ${tally.held === 1 ? 'it cannot' : 'they cannot'} be saved as typed`
      : null,
    tally.refused.length > 0
      ? `${countOf(tally.refused.length, 'answer')} not corrected (${someCells(tally.refused)}) — ${tally.refused.length === 1 ? 'the cell marked says' : 'the cells marked say'} why`
      : null,
    tally.unanswered > 0
      ? `${countOf(tally.unanswered, 'cell was', 'cells were')} not answered — Refresh to read ${tally.unanswered === 1 ? 'it' : 'them'} again`
      : null
  ]
    .filter(Boolean)
    .join(' · ');
}

/** What a batch's member is, in a refusal's words. */
const BATCH_MEMBER: Readonly<Record<string, string>> = { note: 'The note', viewId: 'The view' };

/** survey-api's 400 problems, the cell ones placed by draft and the rest said in words. */
function placeEditProblems(
  error: ApiError,
  sent: readonly ICellDraft[]
): { byCell: Map<string, string>; rest: string[] } {
  const byCell = new Map<string, string>();
  const rest: string[] = [];
  for (const problem of error.problem?.problems ?? []) {
    const at = /^cells\[(\d+)\]/.exec(problem.target);
    const draft = at ? sent[Number(at[1])] : undefined;
    if (draft) {
      const said = byCell.get(draft.key);
      byCell.set(draft.key, said ? `${said}; ${problem.reason}` : problem.reason);
    } else {
      rest.push(`${BATCH_MEMBER[problem.target] ?? 'The save'} ${problem.reason}.`);
    }
  }
  return { byCell, rest };
}

function unanswered(error: unknown): boolean {
  return !(error instanceof ApiError) || error.status >= 500;
}

/** Why a *Keep mine* resend was not kept, lower-case, for its cell's sentence. */
function keptRefusal(error: unknown): string {
  if (unanswered(error)) return 'the answer did not arrive — the list is read again';
  if (error instanceof ApiError && error.status === 403) return 'your Team Portal roles do not allow this save';
  if (error instanceof ApiError && error.status === 400) {
    const reasons = (error.problem?.problems ?? []).map((problem) => problem.reason);
    if (reasons.length > 0) return reasons.join('; ');
  }
  return refusalMessage(error, 'the save was refused');
}

/** The conflict asked about now, with the draft it stands for. */
export interface IAskedConflict {
  conflict: IListEditConflict;
  draft: ICellDraft;
}

/** A row's cell staged with `ui` — on the draft it already holds, if any — or null where the value
 *  is the one stored, which is no change. */
function draftFor(
  current: ReadonlyMap<string, ICellDraft>,
  row: IListRow,
  field: IListField,
  kind: CellEditKind,
  ui: InternalUiValue,
  badInput: boolean
): Nullable<ICellDraft> {
  const answer = answerTagOf(field);
  const key = draftKey({ ...row, field: field.code });
  const base = current.get(key)?.base ?? row.cells[field.code] ?? null;
  const staged = stagedOf(kind, ui, badInput, answer);
  if (!staged.held && sameAsStored(kind, staged.value, base, answer)) return null;
  return {
    key,
    participantId: row.participantId,
    survey: row.survey,
    field: field.code,
    kind,
    answer,
    name: nameOf(row),
    label: field.label,
    ui,
    staged,
    base
  };
}

/** The drafts with `key`'s replaced by `draft`, or dropped where it is null. */
function withDraft(
  current: ReadonlyMap<string, ICellDraft>,
  draft: Nullable<ICellDraft>,
  key: string
): ReadonlyMap<string, ICellDraft> {
  if (draft === null && !current.has(key)) return current;
  const next = new Map(current);
  if (draft === null) next.delete(key);
  else next.set(key, draft);
  return next;
}

export function useListEdits({
  viewId,
  emailOf,
  adopt,
  poll,
  refresh
}: {
  /** The view the screen was opened from — the batch's `viewId`. */
  viewId: Nullable<string>;
  /** An account's email, for an *Assigned to* cell adopted. */
  emailOf: (accountId: string) => string | undefined;
  adopt: (cells: readonly IAdoptedCell[]) => void;
  /** One poll; `after` a save it abandons a poll out since before the save. */
  poll: (after?: boolean) => Promise<boolean>;
  /** The page read afresh — its rows, or null where it failed or another read took its place. */
  refresh: () => Promise<Nullable<readonly IListRow[]>>;
}) {
  const [drafts, setDrafts] = React.useState<ReadonlyMap<string, ICellDraft>>(new Map());
  const [cellErrors, setCellErrors] = React.useState<ReadonlyMap<string, string>>(new Map());
  const [banner, setBanner] = React.useState<Nullable<string>>(null);
  const [status, setStatus] = React.useState<Nullable<string>>(null);
  const [saving, setSaving] = React.useState(false);
  const [asked, setAsked] = React.useState<Nullable<IAskedConflict>>(null);

  const draftsRef = React.useRef(drafts);
  draftsRef.current = drafts;
  const queue = React.useRef<IAskedConflict[]>([]);
  const tally = React.useRef<ITally>(NO_TALLY);
  /** The refused *Keep mine*s of one press, said together. */
  const keptRefused = React.useRef<string[]>([]);
  /** The banner says only that the cells marked cannot be saved — it goes with the last mark. */
  const bannerOfCells = React.useRef(false);
  const cellErrorsRef = React.useRef(cellErrors);
  cellErrorsRef.current = cellErrors;

  const say = (text: Nullable<string>, ofCells = false) => {
    bannerOfCells.current = text !== null && ofCells;
    setBanner(text);
  };

  /** A cell's value as the row holds it now, adopted. */
  const adopted = React.useCallback(
    (draft: ICellDraft, raw: unknown, version: number): IAdoptedCell => ({
      participantId: draft.participantId,
      survey: draft.survey,
      field: draft.field,
      cell:
        raw === null || raw === undefined
          ? null
          : {
              v: raw,
              version,
              label: draft.kind === 'assignee' && typeof raw === 'string' ? (emailOf(raw) ?? null) : undefined
            }
    }),
    [emailOf]
  );

  /** Drops a settled draft — unless the cell was edited again while the save was out, when the newer
   *  draft stays, now based on the cell as it was settled. */
  const settle = (sent: ICellDraft, cell: Nullable<IListCell>) => {
    setDrafts((current) => {
      const now = current.get(sent.key);
      if (now === undefined) return current;
      const next = new Map(current);
      if (now === sent) next.delete(sent.key);
      else next.set(sent.key, { ...now, base: cell });
      return next;
    });
  };

  /** Drops the marks of the cells given — a cell staged again, or sent again, is judged afresh. */
  const unmark = (keys: readonly string[]) => {
    const marks = cellErrorsRef.current;
    if (!keys.some((key) => marks.has(key))) return;
    const next = new Map(marks);
    for (const key of keys) next.delete(key);
    cellErrorsRef.current = next;
    setCellErrors(next);
    if (next.size === 0 && bannerOfCells.current) say(null);
  };

  const stage = React.useCallback(
    (row: IListRow, field: IListField, ui: InternalUiValue, badInput = false) => {
      const kind = editKindOf(field);
      if (kind === null) return;
      const key = draftKey({ ...row, field: field.code });
      setDrafts((current) => withDraft(current, draftFor(current, row, field, kind, ui, badInput), key));
      unmark([key]);
      setStatus(null);
    },
    []
  );

  const discard = React.useCallback(() => {
    setDrafts(new Map());
    setCellErrors(new Map());
    say(null);
    setStatus(null);
  }, []);

  /**
   * After an answer that never arrived, the page as read again: where it shows the batch landed —
   * a sent cell holding the value sent, at a version past the one quoted — every sent draft is
   * settled on the cell as read, dropped where it holds that value. Where nothing shows it landed,
   * the drafts stay as they were, so a colleague's save since is still a conflict.
   */
  const rebase = (rows: readonly IListRow[], sent: readonly ICellDraft[]) => {
    const byRow = new Map(rows.map((row) => [rowKeyOf(row), row]));
    const now = (draft: ICellDraft): Nullable<IListCell> | undefined => {
      const row = byRow.get(rowKeyOf(draft));
      return row === undefined ? undefined : (row.cells[draft.field] ?? null);
    };
    const landed = sent.some((draft) => {
      const cell = now(draft);
      return (
        cell !== undefined &&
        !draft.staged.held &&
        (cell?.version ?? 0) !== (draft.base?.version ?? 0) &&
        sameAsStored(draft.kind, draft.staged.value, cell, draft.answer)
      );
    });
    if (!landed) return;
    setDrafts((current) => {
      const next = new Map(current);
      for (const draft of sent) {
        const cell = now(draft);
        const held = current.get(draft.key);
        if (cell === undefined || held === undefined) continue;
        const settled =
          !held.staged.held && sameAsStored(held.kind, held.staged.value, cell, held.answer);
        if (settled) next.delete(draft.key);
        else next.set(draft.key, { ...held, base: cell });
      }
      return next;
    });
  };

  /** A refusal of a batch: nothing written, every draft kept, the refusal said where it belongs —
   *  beside the marks other drafts hold, where the batch was not every draft (`whole`). */
  const refused = (error: unknown, sent: readonly ICellDraft[], whole: boolean) => {
    if (unanswered(error)) {
      say(`${NOT_ARRIVED} Your changes are kept here.`);
      void refresh().then((rows) => rows && rebase(rows, sent));
      return;
    }
    if (error instanceof ApiError && error.status === 400) {
      const { byCell, rest } = placeEditProblems(error, sent);
      if (whole) setCellErrors(byCell);
      else markRefused(byCell);
      if (rest.length > 0) say(`Nothing was saved. ${rest.join(' ')}`);
      else if (byCell.size > 0) {
        say(
          `Nothing was saved — ${byCell.size === 1 ? 'the cell marked cannot' : 'the cells marked cannot'} be saved as ${byCell.size === 1 ? 'it is' : 'they are'}.`,
          true
        );
      } else say(`Nothing was saved. ${refusalMessage(error, 'The save was refused')}`);
      return;
    }
    if (error instanceof ApiError && error.status === 403) {
      say('Your Team Portal roles do not allow this save — nothing was saved, and your changes are kept here.');
      return;
    }
    say(`Nothing was saved. ${refusalMessage(error, 'The save was refused')}`);
  };

  /** The end of one press: the poll that strikes the rows the save took out, and the status line —
   *  a poll out since before the save is abandoned for it (`after`). */
  const finish = React.useCallback(async () => {
    setSaving(false);
    await poll(true);
    setStatus(tallySentence(tally.current) || null);
  }, [poll]);

  const next = () => {
    const [first, ...rest] = queue.current;
    queue.current = rest;
    setAsked(first ?? null);
    if (first === undefined) void finish();
  };

  /** The answer cells survey-api could not correct, each said on its cell — its draft kept. */
  const markRefused = (refused: ReadonlyMap<string, string>) => {
    if (refused.size === 0) return;
    const next = new Map(cellErrorsRef.current);
    for (const [key, said] of refused) next.set(key, said);
    cellErrorsRef.current = next;
    setCellErrors(next);
  };

  /**
   * survey-api's answer read per cell: the settled cells adopted, the refused ones marked, the
   * conflicts queued. Answers the conflicts, how many sent cells it settled and refused, and how
   * many it named nowhere — those stay drafts, as the refused ones do.
   */
  const read = (result: IListEditResult, sent: readonly ICellDraft[]) => {
    const byKey = new Map(sent.map((draft) => [draft.key, draft]));
    const draftOf = (ref: IListEditCellRef | IListEditConflict | IListEditRefusal) =>
      byKey.get(draftKey(ref));
    const answered = new Set<string>();
    const cells: IAdoptedCell[] = [];
    for (const ref of [...result.applied, ...result.unchanged]) {
      const draft = draftOf(ref);
      if (!draft || draft.staged.held || answered.has(draft.key)) continue;
      answered.add(draft.key);
      const cell = adopted(draft, rawOf(draft.kind, draft.staged.value), ref.version);
      cells.push(cell);
      settle(draft, cell.cell);
    }
    adopt(cells);
    const settled = answered.size;
    const refused = new Map<string, string>();
    const refusedNames: string[] = [];
    for (const refusal of result.refused ?? []) {
      const draft = draftOf(refusal);
      if (!draft || answered.has(draft.key)) continue;
      answered.add(draft.key);
      refused.set(draft.key, refusedCellText(refusal.code));
      refusedNames.push(`${draft.label} for ${draft.name}`);
    }
    markRefused(refused);
    const conflicts = result.conflicts.flatMap((conflict) => {
      const draft = draftOf(conflict);
      if (!draft || answered.has(draft.key)) return [];
      answered.add(draft.key);
      return [{ conflict, draft }];
    });
    return {
      conflicts,
      settled,
      refused: refusedNames,
      unanswered: sent.filter((draft) => !answered.has(draft.key)).length
    };
  };

  /** One press's batch: the drafts given, sent together, their answer read as *Save data*'s —
   *  `whole` where they are every draft, whose marks all go. */
  const send = async (pending: readonly ICellDraft[], whole: boolean) => {
    const sent = pending.filter((draft) => !draft.staged.held);
    tally.current = { ...NO_TALLY, held: pending.length - sent.length };
    keptRefused.current = [];
    // A part's press leaves the banner that speaks for other drafts' marks.
    if (whole || !bannerOfCells.current) say(null);
    setStatus(null);
    if (sent.length === 0) {
      setStatus(tallySentence(tally.current) || null);
      return;
    }
    const cells: IListEditCell[] = sent.map((draft) => ({
      participantId: draft.participantId,
      survey: draft.survey,
      field: draft.field,
      value: draft.staged.held ? null : draft.staged.value,
      version: draft.base?.version ?? 0
    }));
    setSaving(true);
    if (whole) setCellErrors(new Map());
    else unmark(sent.map((draft) => draft.key));
    try {
      const result = await listsApi.edit({ viewId, cells });
      const answer = read(result, sent);
      tally.current.saved = answer.settled;
      tally.current.refused = answer.refused;
      tally.current.unanswered = answer.unanswered;
      queue.current = answer.conflicts;
      next();
    } catch (error) {
      setSaving(false);
      refused(error, sent, whole);
    }
  };

  // `send` reads only refs and setters.
  const save = React.useCallback(
    () => send([...draftsRef.current.values()], true),
    [viewId, adopt, adopted, finish, refresh]
  );

  /**
   * *Assign the selected rows to…*'s press (SR-491, LST-11): the value staged on each row and those
   * cells saved at once, as *Save data* would save them — conflicts asked, refusals marked — while
   * every other staged cell stays staged. A row already holding the value sends nothing.
   */
  const saveNow = React.useCallback(
    async (rows: readonly IListRow[], field: IListField, ui: InternalUiValue) => {
      const kind = editKindOf(field);
      if (kind === null) return;
      const current = draftsRef.current;
      const entries = rows.map((row) => ({
        key: draftKey({ ...row, field: field.code }),
        draft: draftFor(current, row, field, kind, ui, false)
      }));
      setDrafts((now) => entries.reduce((next, { key, draft }) => withDraft(next, draft, key), now));
      await send(
        entries.flatMap(({ draft }) => (draft ? [draft] : [])),
        false
      );
    },
    [viewId, adopt, adopted, finish, refresh]
  );

  /** *Keep mine*: that cell alone, again, at the version it holds now, with the note saying so. */
  const keepMine = React.useCallback(
    async ({ conflict, draft }: IAskedConflict) => {
      setAsked(null);
      try {
        const result = await listsApi.edit({
          viewId,
          note: `Kept over ${authorOf(conflict.current)}’s save`,
          cells: [
            {
              participantId: conflict.participantId,
              survey: conflict.survey,
              field: conflict.field,
              value: conflict.mine,
              version: conflict.current.version
            }
          ]
        });
        const answer = read(result, [draft]);
        tally.current.kept += answer.settled;
        tally.current.refused = [...tally.current.refused, ...answer.refused];
        tally.current.unanswered += answer.unanswered;
        // Saved over again since — asked about once more, first.
        queue.current = [...answer.conflicts, ...queue.current];
      } catch (error) {
        // The draft stays, at the version it was read at: the next press asks about it again.
        keptRefused.current = [
          ...keptRefused.current,
          `${draft.label} for ${draft.name} was not kept — ${keptRefusal(error)}.`
        ];
        say(keptRefused.current.join(' '));
        if (unanswered(error)) void refresh().then((rows) => rows && rebase(rows, [draft]));
      }
      next();
    },
    [viewId, adopt, adopted, finish, refresh]
  );

  /** *Take theirs* — and *Cancel*, and the dialog closed any other way (AC3): `current` drawn,
   *  nothing sent. */
  const takeTheirs = React.useCallback(
    ({ conflict, draft }: IAskedConflict) => {
      const cell = adopted(draft, currentRaw(draft.kind, conflict.current), conflict.current.version);
      adopt([cell]);
      settle(draft, cell.cell);
      tally.current.taken += 1;
      next();
    },
    [adopt, adopted, finish]
  );

  return {
    drafts,
    cellErrors,
    banner,
    status,
    saving: saving || asked !== null,
    asked,
    stage,
    discard,
    save,
    saveNow,
    keepMine,
    takeTheirs,
    /** Clears what the last press said — a new query reads a new list. */
    clearSaid: React.useCallback(() => {
      setStatus(null);
      say(null);
    }, [])
  };
}
